import type {
  ActionConfig,
  ActionReference,
  LoopRecordListActionGroupConfig,
  ValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import {
  DateKind,
  Timestamp,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../types/candidate";

import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import type { CandidateStore } from "../../../../../../../stores/automation/candidate.store";
import {
  ActionLabel,
  TriggerLabel,
} from "../../../../../../../utils/constants";
import * as R from "remeda";
import type { Option } from "./LinkEditor/utils";
import type { Fields } from "@smartsuite/smartsuite-client";
import {
  getLabel,
  getOutputDescriptions,
} from "../../../../../../../utils/conditions-coverter.hook";
import type { LoopScope } from "../../../../../../../stores/automation/looping.store";
import type { TriggerActionOption } from "../../../MemberEditor/utils";

export const calculateConfigValue = (
  newPills: PillNodeAttrs[],
  actions: ActionCandidate[],
  trigger: TriggerCandidate,
  matching = false,
): ValueConfig => {
  // @ts-expect-error
  const values: ValueConfig_V[] = newPills
    .map((pill) => {
      const value = JSON.parse(String(pill.value));
      if (value.target === "option") {
        return {
          value: {
            oneofKind: "option",
            option: {
              value: value.value,
              // @ts-expect-error
              label: pill.origTitle ?? pill.title,
              color: value.color ?? "",
            },
          },
        };
      } else if (value.target === "tag") {
        return {
          value: {
            oneofKind: "tag",
            tag: { id: value.value, label: pill.title },
          },
        };
      } else if (value.target === "date") {
        return {
          value: {
            oneofKind: "date",
            date: Timestamp.fromDate(new Date(value.valueUTC)),
          },
          dateKind: DateKind.DATE,
        };
      } else if (value.target === "date_function") {
        return {
          value: {
            oneofKind: "function",
            function: {
              function: {
                oneofKind: "now",
                now: {
                  offset: value.value,
                },
              },
            },
          },
          dateKind: DateKind.DATE,
        };
      } else if (value.target === "date_with_time_function") {
        return {
          value: {
            oneofKind: "function",
            function: {
              function: {
                oneofKind: "nowWithTime",
                nowWithTime: {
                  offset: value.value,
                },
              },
            },
          },
          dateKind: DateKind.DATETIME,
        };
      } else if (value.target === "date_offset_function") {
        return {
          value: {
            oneofKind: "function",
            function: {
              function: {
                oneofKind: "dateAddOffset",
                dateAddOffset: {
                  startOfDay: value.startOfDay,
                  offset: value.offset,
                  value: calculateConfigValue(
                    [
                      {
                        ...pill,
                        value: JSON.stringify({
                          hash: value.hash,
                          value: value.value,
                          valueUTC: value.valueUTC,
                          target: value.originalTarget,
                        }),
                      },
                    ],
                    actions,
                    trigger,
                  ),
                },
              },
            },
          },
        };
      } else if (value.target === "action") {
        return {
          value: {
            oneofKind: "reference",
            reference: {
              outputId: String(value.value),
              source: {
                oneofKind: "actionReference",
                actionReference: actions.find(
                  (action) => action.hash === value.hash,
                )?.actionReference,
              },
            },
          },
        };
      } else if (
        value.target === "trigger" &&
        trigger.triggerReference !== undefined
      ) {
        return matching
          ? {
              value: {
                oneofKind: "matchingReference",
                matchingReference: {
                  outputId: value.value,
                  source: {
                    oneofKind: "triggerReference",
                    triggerReference: trigger.triggerReference,
                  },
                },
              },
            }
          : {
              value: {
                oneofKind: "reference",
                reference: {
                  outputId: value.value,
                  source: {
                    oneofKind: "triggerReference",
                    triggerReference: trigger.triggerReference,
                  },
                },
              },
            };
      } else if (value.target === "file") {
        return {
          value: {
            oneofKind: "file",
            file: value.value,
          },
        };
      } else if (value.target === "loop") {
        if (value.value?.oneofKind === "outputReference") {
          return {
            value: {
              oneofKind: "reference",
              reference: value.value.outputReference,
            },
          };
        }
      }
      return undefined;
    })
    .filter((item) => item !== undefined);
  return { values };
};

export const isValueNotValid = (
  candidate: CandidateStore,
  value?: ValueConfig,
): boolean =>
  candidate.isTriggerSubmitted &&
  candidate.self.trigger.triggerReference?.triggerId ===
    TriggerLabel.matchesCondition &&
  (R.isNil(value) || value.values.length === 0);

export const actionsToOptions = (
  actions: ActionCandidate[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
): Option[] => {
  return actions.flatMap((action) => {
    return getOutputDescriptions(action)
      .filter((output) => {
        let pass =
          output.value?.type === undefined
            ? true
            : types.includes(output.value?.type);
        if (ssFieldTypes !== undefined) {
          pass =
            output.smartsuiteFieldType === undefined ||
            (ssFieldTypes.includes(output.smartsuiteFieldType as Fields) &&
              (output.value?.type === undefined ||
                types.includes(output.value?.type)));
        }
        return pass;
      })
      .map((output) => {
        return {
          title: getLabel(output),
          outputId: output.outputId,
          icon: output.icon,
        };
      });
  });
};

export const getSourceActionReference = (
  source: LoopRecordListActionGroupConfig["source"] | undefined,
): ActionReference | undefined => {
  if (
    source?.oneofKind === "recordList" &&
    source.recordList.recordList.oneofKind === "actionReference"
  ) {
    return source.recordList.recordList.actionReference;
  }
  return undefined;
};

export const getLoopingSourceActionReference = (
  looping: LoopScope,
): ActionReference | undefined => getSourceActionReference(looping.source);

export const checkIfIsPickerInputField = (
  target: any,
  inputId: string,
): boolean => {
  const pickerClass = `picker-input-field-${inputId}`;
  const dateSelectClass = `date-select__option`; // year picker
  return (
    target?.classList?.contains(pickerClass) ||
    target?.classList?.contains(dateSelectClass) ||
    target?.parentElement?.classList?.contains(pickerClass)
  );
};

export const ifLoopingSourceIsActionFind = (looping: LoopScope): boolean => {
  return (
    looping?.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference" &&
    looping.source.recordList.recordList.actionReference.actionId ===
      ActionLabel.findRecords
  );
};
